import type {
  ReportingChartSpec,
  ReportingDataCell,
  ReportingDataset,
  ReportingDatasetRow,
} from 'src/agent/reporting/reportingPresentation.types'

import type { ReportingRenderRow } from './reportingChart.types'
import {
  categoryValue,
  columnFor,
  encodingFor,
  INVALID_VALUE,
  rawValueFor,
  safeNumericValue,
  valueKey,
} from './reportingChartData'

const compareValues = (
  left: ReportingDataCell,
  right: ReportingDataCell,
): number => {
  if (left === right) return 0
  if (left === null) return 1
  if (right === null) return -1

  const leftNumber = safeNumericValue(left)
  const rightNumber = safeNumericValue(right)
  if (
    leftNumber !== INVALID_VALUE &&
    rightNumber !== INVALID_VALUE &&
    leftNumber !== null &&
    rightNumber !== null
  ) {
    return leftNumber - rightNumber
  }

  return String(left).localeCompare(String(right))
}

export const sortedRows = (
  chartSpec: ReportingChartSpec,
  dataset: ReportingDataset,
): ReportingDatasetRow[] | null => {
  const sort = chartSpec.sort
  if (sort !== undefined && !Array.isArray(sort)) return null
  const sortItems = Array.isArray(sort) ? sort : []
  for (const item of sortItems) {
    if (
      !item ||
      typeof item !== 'object' ||
      typeof (item as Record<string, unknown>).field !== 'string' ||
      ((item as Record<string, unknown>).direction !== 'ascending' &&
        (item as Record<string, unknown>).direction !== 'descending') ||
      !columnFor(dataset, (item as Record<string, unknown>).field as string)
    ) {
      return null
    }
  }

  if (sortItems.length === 0) return [...dataset.rows]

  return [...dataset.rows].sort((left, right) => {
    for (const item of sortItems) {
      const sortItem = item as Record<string, unknown>
      const field = sortItem.field as string
      const leftValue = rawValueFor(left, field)
      const rightValue = rawValueFor(right, field)
      if (leftValue === INVALID_VALUE || rightValue === INVALID_VALUE) return 0
      const comparison = compareValues(leftValue, rightValue)
      if (comparison !== 0) {
        return sortItem.direction === 'descending' ? -comparison : comparison
      }
    }
    return 0
  })
}

export const rowsWithTopNOther = (
  chartSpec: ReportingChartSpec,
  rows: ReportingDatasetRow[],
): ReportingDatasetRow[] | null => {
  const transform = chartSpec.top_n_other
  if (!transform || rows.length <= transform.limit) return rows
  if (
    !Number.isInteger(transform.limit) ||
    transform.limit < 1 ||
    transform.limit > 25 ||
    transform.label !== 'Other'
  ) {
    return null
  }

  const categoryReference =
    chartSpec.kind === 'pie'
      ? encodingFor(chartSpec, 'category')
      : chartSpec.kind === 'horizontal_bar'
        ? encodingFor(chartSpec, 'y')
        : undefined
  const valueReference =
    chartSpec.kind === 'pie'
      ? encodingFor(chartSpec, 'value')
      : chartSpec.kind === 'horizontal_bar'
        ? encodingFor(chartSpec, 'x')
        : undefined
  if (!categoryReference || !valueReference) return null

  let other = 0
  for (const row of rows.slice(transform.limit)) {
    const value = safeNumericValue(rawValueFor(row, valueReference.field))
    if (value === INVALID_VALUE || value === null) return null
    other += value
  }
  const remainderSource = rows[transform.limit]
  return [
    ...rows.slice(0, transform.limit),
    {
      ...remainderSource,
      [categoryReference.field]: transform.label,
      [valueReference.field]: other,
    },
  ]
}

export const hasNumericValue = (
  data: ReportingRenderRow[],
  keys: readonly string[],
): boolean =>
  keys.some((key) => data.some((row) => typeof row[key] === 'number'))

const makeSeriesKey = (index: number): string => `series_${index}`

interface PivotOptions {
  categoryField: string
  valueField: string
  seriesField?: string
}

export const pivotRows = (
  rows: ReportingDatasetRow[],
  options: PivotOptions,
): {
  data: ReportingRenderRow[]
  valueDataKeys: string[]
  seriesLabels: Record<string, string>
} | null => {
  const { categoryField, valueField, seriesField } = options
  const groups = new Map<string, ReportingRenderRow>()
  const seriesKeys = new Map<string, string>()
  const seriesLabels: Record<string, string> = {}

  for (const row of rows) {
    const rawCategory = rawValueFor(row, categoryField)
    const rawValue = rawValueFor(row, valueField)
    if (rawCategory === INVALID_VALUE || rawValue === INVALID_VALUE) return null
    const category = categoryValue(rawCategory)
    const numeric = safeNumericValue(rawValue)
    if (category === INVALID_VALUE || numeric === INVALID_VALUE) return null

    let seriesKey = 'value'
    if (seriesField) {
      const rawSeries = rawValueFor(row, seriesField)
      if (rawSeries === INVALID_VALUE) return null
      const series = categoryValue(rawSeries)
      if (series === INVALID_VALUE) return null
      const seriesId = valueKey(series)
      seriesKey = seriesKeys.get(seriesId) ?? makeSeriesKey(seriesKeys.size)
      if (!seriesKeys.has(seriesId)) {
        seriesKeys.set(seriesId, seriesKey)
        seriesLabels[seriesKey] = String(series)
      }
    }

    const groupId = valueKey(category)
    const group = groups.get(groupId) ?? { [categoryField]: category }
    if (Object.prototype.hasOwnProperty.call(group, seriesKey)) return null
    group[seriesKey] = numeric
    groups.set(groupId, group)
  }

  const valueDataKeys = seriesField ? [...seriesKeys.values()] : ['value']
  const data = [...groups.values()]
  return { data, valueDataKeys, seriesLabels }
}
